import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '@/lib/utils'

/**
 * Zoradenie v tvare, aký očakáva DRF: `sname` vzostupne, `-sname` zostupne.
 * Držíme ho ako jeden reťazec, nie ako dvojicu pole + smer — do dotazu ide
 * tak či tak jedna hodnota a dva stavy by sa mohli rozísť.
 */
export type Ordering = string

export function useOrdering(defaultOrdering: Ordering, storageKey?: string) {
  const [ordering, setOrdering] = useState<Ordering>(() => {
    if (!storageKey) return defaultOrdering
    return localStorage.getItem(storageKey) || defaultOrdering
  })

  const change = (next: Ordering) => {
    setOrdering(next)
    if (storageKey) localStorage.setItem(storageKey, next)
  }

  /**
   * Tri stavy, nie dva: vzostupne → zostupne → **späť na predvolené**.
   *
   * Bez tretieho kroku sa raz zvolené zoradenie nedá zrušiť inak než
   * vyčistením pamäte prehliadača - a pri inštruktoroch je predvolené poradie
   * to vlastné, ručne nastavené, takže sa doň treba vedieť vrátiť.
   */
  const toggle = (field: string) => {
    if (ordering === field) return change(`-${field}`)
    if (ordering === `-${field}`) return change(defaultOrdering)
    return change(field)
  }

  return {
    ordering,
    setOrdering: change,
    toggle,
    reset: () => change(defaultOrdering),
    isDefault: ordering === defaultOrdering,
  }
}

export function SortableHeader({
  field,
  label,
  ordering,
  onSort,
  align = 'left',
  className,
}: {
  /** Názov poľa v API. `undefined` = stĺpec sa zoraďovať nedá. */
  field?: string
  label: string
  ordering: Ordering
  onSort: (field: string) => void
  align?: 'left' | 'right'
  className?: string
}) {
  const { t } = useTranslation()

  if (!field) {
    return (
      <th className={cn(align === 'right' && 'text-right', className)}>{label}</th>
    )
  }

  const active = ordering === field || ordering === `-${field}`
  const descending = ordering === `-${field}`
  const Icon = !active ? ArrowUpDown : descending ? ArrowDown : ArrowUp

  // Čo urobí ďalšie kliknutie - bez toho nie je z ničoho vidieť, že tretí klik
  // zoradenie zruší a vráti predvolený stav.
  const next = !active ? 'sortAsc' : descending ? 'sortClear' : 'sortDesc'

  return (
    <th className={cn(align === 'right' && 'text-right', className)}>
      <button
        type="button"
        onClick={() => onSort(field)}
        title={t(`global.${next}`)}
        aria-sort={!active ? 'none' : descending ? 'descending' : 'ascending'}
        className={cn(
          'inline-flex items-center gap-1 whitespace-nowrap hover:text-slate-700',
          align === 'right' && 'flex-row-reverse',
          active && 'text-brand-600',
        )}
      >
        {label}
        <Icon size={12} className={cn(!active && 'opacity-30')} />
      </button>
    </th>
  )
}
